/**
 * Insights — compliance trend over time + the executive scorecard.
 *
 * This is the page a CISO opens to answer two questions the point-in-time
 * dashboard cannot:
 *
 *   1. "Are we getting better?" — the trend chart, drawn from stored daily
 *      snapshots. Real history, not a synthetic sparkline: the number for the
 *      1st of March is the number that was true on the 1st of March, written
 *      down that day, because once a gap closes it can never be recomputed.
 *   2. "Where do we stand right now, at a glance?" — the scorecard: overall
 *      compliance, remediation load, evidence currency, vendor risk, incidents,
 *      each a tile with a one-word verdict.
 *
 * One fetch (`/insights/trend`) carries both the series and the current
 * scorecard, so the page is a pure function of that payload.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useApiError } from '../lib/useApiError';
import { Card, CardHeader, StatTile, Skeleton, ErrorNotice, type Tone } from '../components/ui';
import { TrendArea, type TrendDatum } from '../components/charts';
import { IconReport, IconGap, IconPlan, IconShield, IconVendor, IconIncident } from '../components/icons';

interface ScorecardKpis {
  overallCompliance: number | null;
  assessedFrameworks: number;
  licensedFrameworks: number;
  frameworkRates: Record<string, number>;
  openGaps: number;
  gapsNotInPlan: number;
  openPlans: number;
  overduePlans: number;
  activePolicies: number;
  vendorsAtRisk: number;
  openIncidents: number;
  evidenceCurrentPct: number | null;
}

interface TrendPoint {
  takenAt: string;
  takenOn: string;
  overallCompliance: number | null;
  frameworkRates: Record<string, number>;
  openGaps: number;
  openPlans: number;
  overduePlans: number;
  vendorsAtRisk: number;
  openIncidents: number;
  source: string;
}

interface TrendResponse {
  series: TrendPoint[];
  scorecard: ScorecardKpis;
  delta: { from: number | null; to: number | null; change: number | null };
}

/** Verdict tone for a 0–100 compliance rate — shared threshold with the dashboard. */
function toneForRate(rate: number): Tone {
  return rate >= 80 ? 'good' : rate >= 50 ? 'serious' : 'critical';
}

export function InsightsPage() {
  const { t, i18n } = useTranslation();
  const describeError = useApiError();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['insights', 'trend'],
    queryFn: () => api.get<TrendResponse>('/insights/trend'),
  });

  if (isLoading) return <InsightsSkeleton />;

  if (error) {
    return (
      <div className="oc-page">
        <ErrorNotice message={describeError(error)} onRetry={() => void refetch()} retryLabel={t('common.retry')} />
      </div>
    );
  }
  if (!data) return null;

  const { scorecard, series, delta } = data;

  const dateFmt = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  const dateFmtLong = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });

  const chartData: TrendDatum[] = series.map((p) => ({
    key: p.takenOn,
    label: dateFmt(p.takenAt),
    longLabel: dateFmtLong(p.takenAt),
    value: p.overallCompliance,
    seed: p.source === 'SEED',
  }));

  const overall = scorecard.overallCompliance;
  const overallTone: Tone = overall !== null ? toneForRate(overall) : 'neutral';
  const overdueTone: Tone = scorecard.overduePlans > 0 ? 'critical' : 'good';
  const vendorTone: Tone = scorecard.vendorsAtRisk > 0 ? 'serious' : 'good';
  const incidentTone: Tone = scorecard.openIncidents > 0 ? 'serious' : 'good';
  const evidenceTone: Tone =
    scorecard.evidenceCurrentPct === null
      ? 'neutral'
      : scorecard.evidenceCurrentPct >= 80
        ? 'good'
        : scorecard.evidenceCurrentPct >= 50
          ? 'serious'
          : 'critical';

  // The "27% → 45%" headline, shown only when there are two measured points to
  // compare. Never invented from a single data point.
  const hasDelta = delta.from !== null && delta.to !== null && delta.change !== null;
  const trendSubtitle = hasDelta
    ? t('insights.trendDelta', {
        from: delta.from,
        to: delta.to,
        defaultValue: '{{from}}% → {{to}}% over the recorded period',
      })
    : t('insights.trendSubtitle', 'Overall compliance, captured daily');

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{t('insights.title', 'Insights')}</h1>
        <p className="oc-muted mt-1">
          {t('insights.subtitle', 'How your compliance posture is trending, and where it stands today.')}
        </p>
      </header>

      {/* Executive scorecard */}
      <section aria-label={t('insights.scorecardTitle', 'Executive scorecard')}>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
          <StatTile
            label={t('dashboard.overallCompliance', 'Overall compliance')}
            value={overall !== null ? `${overall}%` : '—'}
            hint={t('insights.assessedOf', {
              assessed: scorecard.assessedFrameworks,
              licensed: scorecard.licensedFrameworks,
              defaultValue: '{{assessed}} of {{licensed}} frameworks assessed',
            })}
            tone={overallTone}
            icon={<IconReport className="w-4 h-4" />}
          />
          <StatTile
            label={t('dashboard.openGaps', 'Open gaps')}
            value={scorecard.openGaps}
            hint={t('insights.gapsNotInPlan', {
              count: scorecard.gapsNotInPlan,
              defaultValue: '{{count}} not yet in a plan',
            })}
            tone="neutral"
            icon={<IconGap className="w-4 h-4" />}
          />
          <StatTile
            label={t('insights.remediation', 'Remediation')}
            value={scorecard.openPlans}
            hint={
              scorecard.overduePlans > 0
                ? t('insights.plansOverdue', { count: scorecard.overduePlans, defaultValue: '{{count}} overdue' })
                : t('insights.noneOverdue', 'none overdue')
            }
            tone={overdueTone}
            icon={<IconPlan className="w-4 h-4" />}
          />
          <StatTile
            label={t('insights.evidenceCurrent', 'Evidence current')}
            value={scorecard.evidenceCurrentPct !== null ? `${scorecard.evidenceCurrentPct}%` : '—'}
            hint={
              scorecard.evidenceCurrentPct === null
                ? t('insights.noEvidenceTracked', 'no expiring evidence tracked')
                : t('insights.evidenceInForce', 'in force (not expired)')
            }
            tone={evidenceTone}
            icon={<IconShield className="w-4 h-4" />}
          />
          <StatTile
            label={t('insights.vendorsAtRisk', 'Vendors at risk')}
            value={scorecard.vendorsAtRisk}
            hint={t('insights.vendorsAtRiskHint', 'high or critical composite risk')}
            tone={vendorTone}
            icon={<IconVendor className="w-4 h-4" />}
          />
          <StatTile
            label={t('insights.openIncidents', 'Open incidents')}
            value={scorecard.openIncidents}
            hint={t('insights.openIncidentsHint', 'not yet resolved or closed')}
            tone={incidentTone}
            icon={<IconIncident className="w-4 h-4" />}
          />
        </div>
      </section>

      {/* Trend chart */}
      <Card>
        <CardHeader
          title={t('insights.trendTitle', 'Compliance over time')}
          subtitle={trendSubtitle}
        />
        <TrendArea
          data={chartData}
          max={100}
          valueSuffix="%"
          emptyLabel={t('insights.trendEmpty', 'No history yet — the first snapshot is captured overnight.')}
        />
        {chartData.some((d) => d.seed) && (
          <p className="text-xs text-ink-muted mt-3">
            {t('insights.seedNote', 'Hollow points are sample values derived from today’s figure to seed the chart, not measured history.')}
          </p>
        )}
      </Card>
    </div>
  );
}

/** Skeleton shaped like the real page, so the layout does not jump on load. */
function InsightsSkeleton() {
  return (
    <div className="oc-page space-y-5">
      <div>
        <Skeleton className="h-7 w-40" />
        <Skeleton className="mt-2 h-4 w-72" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-72 rounded-xl" />
    </div>
  );
}
